import React, { useCallback, useContext, useMemo, useRef } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import LinkIconButton from '@atlaskit/button/icon/link';
import LinkButton from '@atlaskit/button/link';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics/use-analytics-events';
import { usePopupInitialFocusRef } from '@atlaskit/teams-app-internal-popup-adaptor/popup-trigger-with-hover';

import { firedAt } from '../../analytics';
import { useFireCrossAppProfileAnalytic } from '../../hooks/useFireCrossAppProfileAnalytic';
import { useUserProfileLink } from '../../hooks/useUserProfileLink';
import { useCardOpenTimer } from '../../store/cardOpenTimer';
import { UserProfileCardContext } from '../../store/context';
import type { PersonProfileData, UserProfileCardAction } from '../../types';
import { getIconForExternalMessageIntegration } from '../utils/externalMessageIntegrationIcons';
import { resolveExternalMessageFromIntegrations } from '../utils/thirdPartyIntegrationsByProduct';
import { ActionMenu, type ActionMenuItem } from './ActionMenu';
import { useKudosMenuItem } from './useKudosMenuItem';

const messages = defineMessages({
	viewProfile: {
		id: 'user-profile-card.action-bar.view-profile',
		defaultMessage: 'View profile',
		description: 'Button label to navigate to a user profile from the profile card',
	},
	sendMessageLabel: {
		id: 'user-profile-card.action-bar.send-message-label',
		defaultMessage: 'Message',
		description:
			'Accessible label for the send message button (e.g. Slack or Microsoft Teams) on the profile card',
	},
});

interface HeaderActionsProps {
	profileData: PersonProfileData;
	cloudId: string;
	canViewProfile?: boolean;
	/**
	 * Consumer-supplied actions rendered inside the action menu in the top-right
	 * of the header. Each action becomes a menu item; entries whose `shouldRender(profileData)`
	 * returns `false` are filtered out.
	 */
	actions?: UserProfileCardAction[];
	/**
	 * When `true` the user's account is deactivated: only the "View profile" button is rendered.
	 * The third-party message button, custom action menu, and kudos entry are all suppressed.
	 */
	isDeactivated?: boolean;
	/** Identity of the viewer. Used to suppress the kudos entry when viewing one's own profile. */
	viewerId?: string;
}

/**
 * Primary actions for the profile card header (org entry + view profile in Teams) plus an action
 * menu in the top-right that exposes consumer-supplied custom actions and a "Give kudos" entry
 * (always on, with self-hover suppression).
 */
export function HeaderActions({
	profileData,
	cloudId,
	canViewProfile = true,
	actions,
	viewerId,
	isDeactivated = false,
}: HeaderActionsProps): React.JSX.Element | null {
	const intl = useIntl();
	const { fireEvent } = useAnalyticsEvents();
	const { getDurationSinceInteraction } = useCardOpenTimer();
	const { townsquareApp, onProfileClick, getTestId, triggerMethod } =
		useContext(UserProfileCardContext);
	const setPopupInitialFocusRef = usePopupInitialFocusRef();
	// On the top layer, the popup reads its focus target only as it opens. The card loads later,
	// so focus "View profile" when it mounts.
	const focusViewProfile = useCallback(
		(element: HTMLElement | null) => {
			setPopupInitialFocusRef(element);
			element?.focus();
		},
		[setPopupInitialFocusRef],
	);

	const viewProfileInitialFocusRef =
		triggerMethod === 'click' && fg('teams_a11y_focus_high_priority')
			? fg('platform-dst-top-layer')
				? focusViewProfile
				: setPopupInitialFocusRef
			: undefined;
	const { accountId, thirdPartyIntegrations, email } = profileData;
	const { href, target, onNavigate, isCrossAppMauEnabled } = useUserProfileLink(
		accountId,
		cloudId,
		townsquareApp,
	);
	const fireCrossAppProfileAnalytic = useFireCrossAppProfileAnalytic(townsquareApp);

	const externalMessage = useMemo(
		() => resolveExternalMessageFromIntegrations(thirdPartyIntegrations, email),
		[email, thirdPartyIntegrations],
	);

	// We reuse the existing `@atlaskit/profilecard` clicked.action event so the SLO dashboards see a
	// single click-event surface for both profile-card packages. `index` and `hasOnClick` are
	// required by that schema but are not meaningful for user-profile-card actions, so we always
	// send `index: 0` and `hasOnClick: true`.
	const handleThirdPartyMessageClick = useCallback(
		(event: React.MouseEvent) => {
			if (event.type === 'auxclick' && event.button !== 1) {
				return;
			}
			fireEvent('ui.profilecard.clicked.action', {
				firedAt: firedAt(),
				duration: getDurationSinceInteraction() ?? 0,
				hasHref: Boolean(externalMessage?.dm.openDmUrl),
				hasOnClick: true,
				index: 0,
				actionId: 'thirdPartyMessage',
				isAuxClick: event.type === 'auxclick',
			});
		},
		[fireEvent, getDurationSinceInteraction, externalMessage],
	);

	const handleViewProfileClick = useCallback(
		(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
			if (event.type === 'auxclick' && event.button !== 1) {
				return;
			}
			const auxClick = event.type === 'auxclick';
			fireEvent('ui.profilecard.clicked.action', {
				firedAt: firedAt(),
				duration: getDurationSinceInteraction() ?? 0,
				hasHref: Boolean(href),
				hasOnClick: true,
				index: 0,
				actionId: 'viewProfile',
				isAuxClick: auxClick,
			});
			if (isCrossAppMauEnabled) {
				fireCrossAppProfileAnalytic();
			}
			onProfileClick?.(accountId);
			// A middle-click on a link opens a new tab natively, so only run SPA navigation for
			// primary clicks.
			if (!auxClick) {
				onNavigate?.(event);
			}
		},
		[
			fireEvent,
			getDurationSinceInteraction,
			href,
			onNavigate,
			onProfileClick,
			isCrossAppMauEnabled,
			fireCrossAppProfileAnalytic,
			accountId,
		],
	);

	const menuItems = useMemo<ActionMenuItem[]>(() => {
		const visible = (actions ?? []).filter(
			(action) => !action.shouldRender || action.shouldRender(profileData),
		);

		return visible.map((action, index) => {
			const id = action.id ?? `action-${index}`;
			if (action.link) {
				return {
					kind: 'link',
					id,
					label: action.label,
					href: action.link,
					target: action.target,
					onClick: action.callback
						? (event: React.MouseEvent | React.KeyboardEvent, ...args: any[]) => {
								const isBasicClick = !(
									(event as React.MouseEvent).metaKey ||
									(event as React.MouseEvent).altKey ||
									(event as React.MouseEvent).ctrlKey ||
									(event as React.MouseEvent).shiftKey
								);
								if (isBasicClick) {
									event.preventDefault();
									action.callback?.(event, ...args);
								}
							}
						: undefined,
				};
			}
			return {
				kind: 'button',
				id,
				label: action.label,
				onClick: (event: React.MouseEvent | React.KeyboardEvent, ...args: any[]) =>
					action.callback?.(event, ...args),
			};
		});
	}, [actions, profileData]);

	// "More actions" button: the kudos drawer returns focus here, as the "Give kudos" item
	// unmounts when the menu closes.
	const actionMenuTriggerRef = useRef<HTMLElement | null>(null);

	const { menuItem: kudosMenuItem, launcher: kudosLauncher } = useKudosMenuItem({
		accountId,
		cloudId,
		viewerId,
		actionMenuTriggerRef,
	});

	const allMenuItems = useMemo<ActionMenuItem[]>(
		// Deactivated accounts only surface the "View profile" button - no custom actions or kudos.
		() => (isDeactivated ? [] : kudosMenuItem ? [...menuItems, kudosMenuItem] : menuItems),
		[isDeactivated, menuItems, kudosMenuItem],
	);

	const hasActionMenu = allMenuItems.length > 0;
	const hasViewProfile = canViewProfile && Boolean(href);
	const hasExternalMessage = !isDeactivated && Boolean(externalMessage);

	if (!accountId || (!hasViewProfile && !hasExternalMessage && !hasActionMenu)) {
		return null;
	}

	const inline = (
		<Inline space="space.100" alignBlock="center">
			{!isDeactivated && externalMessage && (
				<LinkIconButton
					href={externalMessage.dm.openDmUrl}
					target="_blank"
					rel="noopener noreferrer"
					onClick={handleThirdPartyMessageClick}
					onAuxClick={handleThirdPartyMessageClick}
					icon={getIconForExternalMessageIntegration(externalMessage.dm.integration)}
					label={intl.formatMessage(messages.sendMessageLabel)}
					isTooltipDisabled={false}
				/>
			)}
			{hasViewProfile && (
				<LinkButton
					ref={viewProfileInitialFocusRef}
					href={href}
					appearance="default"
					target={target}
					onClick={handleViewProfileClick}
					onAuxClick={handleViewProfileClick}
					rel={target === '_blank' ? 'noopener noreferrer' : undefined}
					testId={getTestId('view-profile-button')}
				>
					{intl.formatMessage(messages.viewProfile)}
				</LinkButton>
			)}
			{hasActionMenu && (
				<ActionMenu
					items={allMenuItems}
					name={profileData.name}
					triggerRef={actionMenuTriggerRef}
				/>
			)}
		</Inline>
	);

	// When there's no kudos launcher to portal-mount, return the bare `<Inline>` element so that
	// callers which expected the legacy single-element return tree continue to see one node (and
	// no extra fragment wrapper). The fragment form is only used when we actually need to render
	// the launcher sibling.
	if (isDeactivated || !kudosLauncher) {
		return inline;
	}

	return (
		<>
			{inline}
			{kudosLauncher}
		</>
	);
}
